import { useId, type KeyboardEvent } from "react";

import { cn } from "@/lib/cn";
import {
  FLOW_NODE_HEIGHT,
  FLOW_NODE_WIDTH,
  labFlowEdgeTurn,
  labFlowNodeTurn,
  type LabFlowEdge,
  type LabFlowNode,
  type LabRunModel,
} from "@/lib/labRunModel";
import { describeRoutingReason } from "@/lib/previewDecisions";
import { EmptyState } from "@/ui/states";

/**
 * The path the conversation actually took, drawn as plain SVG from the model's
 * deterministic layout. Planned-but-unvisited steps are not drawn: the trace
 * does not carry them. Every node and edge is a keyboard-operable button that
 * selects its turn; the same path is also listed in order below the drawing
 * for screen readers and for narrow screens.
 */

type Props = {
  projectId: string;
  model: LabRunModel;
  selectedTurn: number | null;
  onSelectTurn: (turn: number) => void;
};

// `x`/`y` from the model are a node's top-left corner; the box has the model's fixed size.
const NODE_W = FLOW_NODE_WIDTH;
const NODE_H = FLOW_NODE_HEIGHT;

const MARK_GLYPH = { pass: "✓", fail: "✗", mixed: "±", none: "–" } as const;
const MARK_TEXT = {
  pass: "diagnosis passed",
  fail: "diagnosis failed",
  mixed: "diagnosis mixed",
  none: "no diagnosis recorded",
} as const;
const MARK_CLASS = {
  pass: "fill-success-foreground",
  fail: "fill-destructive",
  mixed: "fill-warning-foreground",
  none: "fill-muted-foreground",
} as const;

type Point = { x: number; y: number };

function clip(node: LabFlowNode, toward: Point): Point {
  const cx = node.x + NODE_W / 2;
  const cy = node.y + NODE_H / 2;
  const dx = toward.x - cx;
  const dy = toward.y - cy;
  if (dx === 0 && dy === 0) return { x: cx, y: cy };
  const scale = Math.min(
    dx === 0 ? Infinity : NODE_W / 2 / Math.abs(dx),
    dy === 0 ? Infinity : NODE_H / 2 / Math.abs(dy),
  );
  return { x: cx + dx * scale, y: cy + dy * scale };
}

type EdgeGeometry = { path: string; mid: Point };

function geometry(
  edge: LabFlowEdge,
  nodes: Map<string, LabFlowNode>,
  offset: number,
): EdgeGeometry | null {
  const from = nodes.get(edge.from);
  const to = nodes.get(edge.to);
  if (!from || !to) return null;
  const a = { x: from.x + NODE_W / 2, y: from.y + NODE_H / 2 };
  const b = { x: to.x + NODE_W / 2, y: to.y + NODE_H / 2 };
  const length = Math.hypot(b.x - a.x, b.y - a.y) || 1;
  const ctrl = {
    x: (a.x + b.x) / 2 + (-(b.y - a.y) / length) * offset * 2,
    y: (a.y + b.y) / 2 + ((b.x - a.x) / length) * offset * 2,
  };
  const start = clip(from, ctrl);
  const end = clip(to, ctrl);
  return {
    path: `M ${start.x} ${start.y} Q ${ctrl.x} ${ctrl.y} ${end.x} ${end.y}`,
    mid: {
      x: 0.25 * start.x + 0.5 * ctrl.x + 0.25 * end.x,
      y: 0.25 * start.y + 0.5 * ctrl.y + 0.25 * end.y,
    },
  };
}

function activate(event: KeyboardEvent<SVGGElement>, run: () => void) {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    run();
  }
}

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

export function LabFlowGraphTab({ model, selectedTurn, onSelectTurn }: Props) {
  const markerId = useId();
  const { nodes, edges, width, height } = model.flow;
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const seenPairs = new Map<string, number>();
  const labelOf = (id: string) => byId.get(id)?.label ?? id;

  return (
    <section aria-labelledby="lab-flow-heading" className="space-y-4">
      <h2
        id="lab-flow-heading"
        className="text-base font-semibold text-foreground"
      >
        Flow graph
      </h2>

      {nodes.length === 0 ? (
        <EmptyState
          title="No path to draw yet"
          description="The graph appears once the run has captured a turn with a recorded position."
        />
      ) : (
        <>
          <div className="overflow-x-auto rounded-md border border-border bg-card p-2">
            <svg
              aria-label="Flow graph of the path this run took"
              width={width}
              height={height}
              viewBox={`0 0 ${width} ${height}`}
              className="max-w-none"
            >
              <defs>
                <marker
                  id={markerId}
                  viewBox="0 0 10 10"
                  refX="9"
                  refY="5"
                  markerWidth="7"
                  markerHeight="7"
                  orient="auto-start-reverse"
                >
                  <path
                    d="M 0 0 L 10 5 L 0 10 z"
                    className="fill-muted-foreground"
                  />
                </marker>
              </defs>

              {edges.map((edge) => {
                const pair = `${edge.from}>${edge.to}`;
                const k = seenPairs.get(pair) ?? 0;
                seenPairs.set(pair, k + 1);
                const reverse = edges.some(
                  (other) => other.from === edge.to && other.to === edge.from,
                );
                const geo = geometry(edge, byId, k * 18 + (reverse ? 10 : 0));
                if (geo === null) return null;
                const selected =
                  selectedTurn !== null && edge.turnNumber === selectedTurn;
                const turn = labFlowEdgeTurn(edge);
                const select = () => onSelectTurn(turn);
                return (
                  <g
                    key={edge.id}
                    role="button"
                    tabIndex={0}
                    aria-label={`Step ${edge.order}: ${labelOf(edge.from)} to ${labelOf(edge.to)} at turn ${edge.turnNumber}`}
                    aria-current={selected ? "true" : undefined}
                    className="cursor-pointer outline-none focus-visible:[&_path]:stroke-ring focus-visible:[&_path]:stroke-[3]"
                    onClick={select}
                    onKeyDown={(event) => activate(event, select)}
                  >
                    <path
                      d={geo.path}
                      fill="none"
                      strokeWidth="12"
                      className="stroke-transparent"
                    />
                    <path
                      d={geo.path}
                      fill="none"
                      markerEnd={`url(#${markerId})`}
                      strokeWidth={selected ? 3 : 1.5}
                      className={
                        selected ? "stroke-ring" : "stroke-muted-foreground"
                      }
                    />
                    <circle
                      cx={geo.mid.x}
                      cy={geo.mid.y}
                      r="10"
                      strokeWidth="1"
                      className={cn(
                        "stroke-border",
                        selected ? "fill-accent" : "fill-card",
                      )}
                    />
                    <text
                      x={geo.mid.x}
                      y={geo.mid.y}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontSize="11"
                      className="fill-foreground"
                    >
                      {edge.order}
                    </text>
                  </g>
                );
              })}

              {nodes.map((node) => {
                const selected =
                  selectedTurn !== null && node.turns.includes(selectedTurn);
                const turn = labFlowNodeTurn(node);
                const select = () => onSelectTurn(turn);
                return (
                  <g
                    key={node.id}
                    role="button"
                    tabIndex={0}
                    aria-label={`Node ${node.label}, visited ${node.visits} ${node.visits === 1 ? "time" : "times"}, ${MARK_TEXT[node.mark]}`}
                    aria-current={selected ? "true" : undefined}
                    className="cursor-pointer outline-none focus-visible:[&_rect]:stroke-ring focus-visible:[&_rect]:stroke-[3]"
                    onClick={select}
                    onKeyDown={(event) => activate(event, select)}
                  >
                    <title>{node.label}</title>
                    <rect
                      x={node.x}
                      y={node.y}
                      width={NODE_W}
                      height={NODE_H}
                      rx="8"
                      strokeWidth={selected ? 3 : 1}
                      className={cn(
                        "fill-card",
                        selected ? "stroke-ring" : "stroke-border",
                      )}
                    />
                    <text
                      x={node.x + 10}
                      y={node.y + 19}
                      fontSize="12"
                      fontWeight="600"
                      className="fill-foreground"
                    >
                      {truncate(node.label, 22)}
                    </text>
                    <text
                      x={node.x + 10}
                      y={node.y + 34}
                      fontSize="11"
                      className="fill-muted-foreground"
                    >
                      {node.visits} {node.visits === 1 ? "visit" : "visits"}
                    </text>
                    <text
                      x={node.x + 10}
                      y={node.y + 49}
                      fontSize="11"
                      className={MARK_CLASS[node.mark]}
                    >
                      {MARK_GLYPH[node.mark]} {MARK_TEXT[node.mark]}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>

          <p className="text-sm text-muted-foreground">
            Only steps the conversation visited are drawn. Planned steps it
            never reached are not shown: that needs the Brain to project its
            flow steps.
          </p>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Path
            </h3>
            {edges.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                The conversation stayed on one step; no moves recorded.
              </p>
            ) : (
              <ol className="list-none space-y-1 text-sm text-foreground">
                {edges.map((edge) => (
                  <li key={edge.id} dir="auto">
                    <button
                      type="button"
                      className="text-start underline-offset-2 hover:underline"
                      onClick={() => onSelectTurn(labFlowEdgeTurn(edge))}
                    >
                      {edge.order}. {labelOf(edge.from)} → {labelOf(edge.to)}{" "}
                      (turn {edge.turnNumber}
                      {edge.reasonCode === null ? ", no reason recorded" : ""})
                      {edge.reasonCode !== null
                        ? ` — ${describeRoutingReason(edge.reasonCode)}`
                        : ""}
                    </button>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </>
      )}
    </section>
  );
}
